/**
 * Persona Normalization Utilities
 *
 * This file provides utilities to normalize personas created via API
 * to match the frontend's expected format. This ensures backward compatibility
 * when reading API-created personas without changing how the frontend creates them.
 */

import type { Persona } from "@/types/api/Persona"

/**
 * Maps 2-letter language codes to extended format (with region)
 * This handles old personas that only have "en", "fr", etc.
 */
const LANGUAGE_CODE_DEFAULTS: Record<string, string> = {
  en: "en-US",
  zh: "zh-CN",
  es: "es-ES",
  fr: "fr-FR",
  de: "de-DE",
  ja: "ja-JP",
  pt: "pt-BR",
  hi: "hi-IN",
  ko: "ko-KR",
  ar: "ar-SA",
  ru: "ru-RU",
  it: "it-IT",
  nl: "nl-NL",
  tr: "tr-TR",
  pl: "pl-PL",
  sv: "sv-SE",
  fil: "fil-PH",
  ms: "ms-MY",
  ro: "ro-RO",
  uk: "uk-UA",
  el: "el-GR",
  cs: "cs-CZ",
  da: "da-DK",
  fi: "fi-FI",
  bg: "bg-BG",
  hr: "hr-HR",
  sk: "sk-SK",
  ta: "ta-IN",
  hu: "hu-HU",
  no: "no-NO",
  bs: "bs-BA",
  sr: "sr-RS",
  he: "he-IL",
}

// Unsupported Deepgram Nova-2 languages - auto-convert to "multi"
const UNSUPPORTED_LANGUAGES = ["hr", "ar", "fil", "bs", "sr", "he"]

// Extended variants of unsupported languages
const UNSUPPORTED_EXTENDED_CODES = [
  "hr-HR",
  "ar-SA",
  "ar-AE",
  "ar-EG",
  "ar-MA",
  "fil-PH",
  "bs-BA",
  "sr-RS",
  "sr-Latn",
  "sr-Cyrl",
  "he-IL",
]

/**
 * Normalizes language codes to extended format
 * Unsupported languages convert to "multi" for Deepgram compatibility
 */
export const normalizeLanguage = (lang: string | undefined): string => {
  if (!lang) {
    return "en-US"
  }

  // If already extended format, check if it's supported
  if (lang.includes("-")) {
    // If it's an unsupported extended code, convert to "multi"
    if (UNSUPPORTED_EXTENDED_CODES.includes(lang)) {
      return "multi"
    }
    return lang
  }

  // Check if 2-letter code is unsupported before expanding
  if (UNSUPPORTED_LANGUAGES.includes(lang)) {
    return "multi"
  }

  // Map to default extended format
  return LANGUAGE_CODE_DEFAULTS[lang] || `${lang}-${lang.toUpperCase()}`
}

/**
 * Converts extended language codes back to 2-letter codes for frontend
 * This is the reverse of normalizeLanguage - used when reading API personas
 */
export const toFrontendLanguage = (lang: string | undefined): string => {
  if (!lang) {
    return "en"
  }

  // If already 2-letter format, return as-is
  if (!lang.includes("-")) {
    return lang
  }

  // Extract 2-letter code from extended format (e.g., "en-US" -> "en")
  return lang.split("-")[0]
}

/**
 * Language display name mappings for extended codes
 * Maps both 2-letter and extended codes to human-readable names
 */
const LANGUAGE_DISPLAY_NAMES: Record<string, string> = {
  // Special codes
  multi: "Multilingual (Auto-Detect)",

  // 2-letter codes
  en: "English",
  es: "Spanish",
  fr: "French",
  de: "German",
  hr: "Croatian",
  it: "Italian",
  tr: "Turkish",
  ar: "Arabic",
  hi: "Hindi",
  nl: "Dutch",
  pt: "Portuguese",
  ja: "Japanese",
  zh: "Chinese",
  cs: "Czech",
  fil: "Filipino",
  pl: "Polish",
  ro: "Romanian",
  sk: "Slovak",
  sv: "Swedish",
  ko: "Korean",
  ru: "Russian",
  uk: "Ukrainian",
  el: "Greek",
  da: "Danish",
  fi: "Finnish",
  bg: "Bulgarian",
  ta: "Tamil",
  hu: "Hungarian",
  no: "Norwegian",
  ms: "Malay",
  bs: "Bosnian",
  sr: "Serbian",
  he: "Hebrew",

  // Extended codes with regions
  "en-US": "English (US)",
  "en-GB": "English (UK)",
  "en-AU": "English (Australia)",
  "en-CA": "English (Canada)",
  "en-IN": "English (India)",
  "es-ES": "Spanish (Spain)",
  "es-MX": "Spanish (Mexico)",
  "es-AR": "Spanish (Argentina)",
  "fr-FR": "French (France)",
  "fr-CA": "French (Canada)",
  "de-DE": "German (Germany)",
  "it-IT": "Italian (Italy)",
  "pt-BR": "Portuguese (Brazil)",
  "pt-PT": "Portuguese (Portugal)",
  "bn-IN": "Bengali (India)",
  "zh-CN": "Mandarin",
  "zh-HK": "Cantonese (Hong Kong)",
  "zh-TW": "Chinese (Traditional)",
  "ja-JP": "Japanese (Japan)",
  "ko-KR": "Korean (Korea)",
  "ar-SA": "Arabic (Saudi Arabia)",
  "hi-IN": "Hindi (India)",
  "ru-RU": "Russian (Russia)",
  "nl-NL": "Dutch (Netherlands)",
  "tr-TR": "Turkish (Turkey)",
  "pl-PL": "Polish (Poland)",
  "sv-SE": "Swedish (Sweden)",
  "cs-CZ": "Czech (Czech Republic)",
  "da-DK": "Danish (Denmark)",
  "fi-FI": "Finnish (Finland)",
  "el-GR": "Greek (Greece)",
  "ro-RO": "Romanian (Romania)",
  "sk-SK": "Slovak (Slovakia)",
  "uk-UA": "Ukrainian (Ukraine)",
  "hr-HR": "Croatian (Croatia)",
  "bg-BG": "Bulgarian (Bulgaria)",
  "hu-HU": "Hungarian (Hungary)",
  "no-NO": "Norwegian (Norway)",
  "ta-IN": "Tamil (India)",
  "ms-MY": "Malay (Malaysia)",
  "fil-PH": "Filipino (Philippines)",
}

/**
 * Gets human-readable display name for language code
 * Supports both 2-letter ("en") and extended ("en-US") formats
 */
export const getLanguageDisplayName = (lang: string | undefined): string => {
  if (!lang) {
    return "English"
  }

  // Direct lookup first
  if (LANGUAGE_DISPLAY_NAMES[lang]) {
    return LANGUAGE_DISPLAY_NAMES[lang]
  }

  // If extended format not found, try base language
  if (lang.includes("-")) {
    const base = lang.split("-")[0]
    if (LANGUAGE_DISPLAY_NAMES[base]) {
      return LANGUAGE_DISPLAY_NAMES[base]
    }
  }

  // Fallback: capitalize the code
  return lang.toUpperCase()
}

/**
 * Maps API background_sound values to frontend values
 * API uses different values than frontend for some options
 */
const BACKGROUND_SOUND_API_TO_FRONTEND: Record<string, string> = {
  // API values -> Frontend values
  "coffee-shop": "lounge", // API "coffee-shop" = Frontend "lounge"
  "convention-hall": "crowd", // API "convention-hall" = Frontend "crowd"
  "summer-outdoor": "playground", // API "summer-outdoor" = Frontend "playground"
  "mountain-outdoor": "playground", // API "mountain-outdoor" = Frontend "playground"
  "static-noise": "office", // API "static-noise" = Frontend "office"
  off: "off", // Same in both
}

/**
 * Maps frontend background_sound values to API values
 * Used when displaying what value would be sent to API
 */
const BACKGROUND_SOUND_FRONTEND_TO_API: Record<string, string> = {
  // Frontend values -> API values
  off: "off",
  office: "static-noise",
  lounge: "coffee-shop",
  crowd: "convention-hall",
  airport: "convention-hall", // No direct API equivalent, use convention-hall
  bus: "coffee-shop", // No direct API equivalent, use coffee-shop
  playground: "summer-outdoor",
}

// All valid background sound IDs (must match BACKGROUND_NOISE_TYPES in EvaluationConstants.ts)
const VALID_BACKGROUND_SOUNDS = [
  "off",
  "office",
  "lounge",
  "crowd",
  "airport",
  "bus",
  "playground",
  "doorbell",
  "train-arrival",
  "portable-air-conditioner",
  "skatepark",
  "small-dog-bark",
  "cafe",
  "ferry-and-announcement",
  "heavy-rain",
  "moderate-wind",
  "newborn-baby-crying",
  "office-with-alarm",
  "street-with-sirens",
  "construction-work",
  "backchanneling",
]

// Uploaded ("bring your own") background sounds are stored as "custom:<asset_id>"
// rather than a built-in catalog id. Must match CUSTOM_BACKGROUND_SOUND_PREFIX on
// the backend (shared/background_sounds/catalog.py).
const CUSTOM_BACKGROUND_SOUND_PREFIX = "custom:"

/**
 * Normalizes background_sound from API format to frontend format
 * This allows frontend to display and work with API-created personas
 */
export const normalizeBackgroundSound = (
  apiValue: string | undefined,
): string => {
  if (!apiValue) {
    return "off"
  }

  // Custom (uploaded) sounds aren't in the built-in catalog, so pass the
  // "custom:<asset_id>" value through unchanged. Otherwise it falls through to
  // "off" below, which makes an uploaded sound look unset when reopening the
  // persona modal — and re-saving would then drop the uploaded sound.
  if (apiValue.startsWith(CUSTOM_BACKGROUND_SOUND_PREFIX)) {
    return apiValue
  }

  if (VALID_BACKGROUND_SOUNDS.includes(apiValue)) {
    return apiValue
  }

  // Map API value to frontend value
  return BACKGROUND_SOUND_API_TO_FRONTEND[apiValue] || "off"
}

/**
 * Converts frontend background_sound to API format
 * Use this when you need to display what the API would receive
 */
export const toApiBackgroundSound = (frontendValue: string): string => {
  // Custom uploaded sounds pass through unchanged (no built-in API mapping).
  if (frontendValue.startsWith(CUSTOM_BACKGROUND_SOUND_PREFIX)) {
    return frontendValue
  }
  return BACKGROUND_SOUND_FRONTEND_TO_API[frontendValue] || "off"
}

/**
 * Normalizes a persona object from API format to frontend format
 * This ensures API-created personas work correctly in the frontend
 *
 * USE THIS when displaying personas in UI (e.g., the persona form)
 */
export const normalizePersonaForFrontend = (persona: Persona): Persona => {
  const metadata = persona.metadata

  return {
    ...persona,
    metadata: {
      ...metadata,
      // Convert extended language back to 2-letter for frontend dropdowns
      language: metadata.language
        ? toFrontendLanguage(metadata.language)
        : undefined,
      // Normalize background_sound to frontend format
      background_sound: metadata.background_sound
        ? normalizeBackgroundSound(metadata.background_sound)
        : undefined,
    },
  }
}

/**
 * Normalizes a persona object from API format to extended format
 * This ensures API-created personas work correctly in simulations
 *
 * USE THIS when running simulations
 */
export const normalizePersona = (persona: Persona): Persona => {
  const metadata = persona.metadata

  return {
    ...persona,
    metadata: {
      ...metadata,
      // Keep extended language format for simulations
      language: metadata.language
        ? normalizeLanguage(metadata.language)
        : undefined,
      // Normalize background_sound to frontend format
      background_sound: metadata.background_sound
        ? normalizeBackgroundSound(metadata.background_sound)
        : undefined,
    },
  }
}

/**
 * Checks if a persona was likely created via API vs frontend
 * API personas have specific characteristics
 */
export const isApiCreatedPersona = (persona: Persona): boolean => {
  const metadata = persona.metadata

  // Check for API-specific indicators
  const hasExtendedLanguage = metadata.language?.includes("-")
  const hasApiBackgroundSound =
    metadata.background_sound &&
    [
      "coffee-shop",
      "convention-hall",
      "summer-outdoor",
      "mountain-outdoor",
      "static-noise",
    ].includes(metadata.background_sound)
  const hasVoiceName = metadata.voice_name !== undefined

  return hasExtendedLanguage || hasApiBackgroundSound || hasVoiceName
}

/**
 * Gets a human-readable description of the differences between
 * API and frontend formats for a given field
 */
export const getFormatDifference = (
  field: "language" | "background_sound",
  value: string,
): { frontend: string; api: string; compatible: boolean } => {
  if (field === "language") {
    const normalized = normalizeLanguage(value)
    return {
      frontend: value,
      api: normalized,
      compatible: value === normalized,
    }
  }

  if (field === "background_sound") {
    const apiValue = toApiBackgroundSound(value)
    return {
      frontend: value,
      api: apiValue,
      compatible: value === apiValue,
    }
  }

  return { frontend: value, api: value, compatible: true }
}

export {
  getPersonaVoiceSystemLabel,
  isGoogleChirpPersona,
  isLegacyElevenLabsPersona,
  transformFromV1Response,
  transformToV1CreateRequest,
  transformToV1UpdateRequest,
} from "./persona-v1-transformations"
